import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'react-redux';
import { LoggedState } from '../models/AuthModelsCommon';
import type { RootState } from '../store/store';
import { useAppSelector } from './store-hook';

const getSessionKey = (auth: RootState['auth']): string | undefined =>
  auth.idToken && auth.user?.uid && (auth.logged === LoggedState.Logged || auth.logged === LoggedState.Anonymous)
    ? `${auth.logged}:${auth.user.uid}` : undefined;

/** Invalidate popup callbacks on logout, account changes, blur, or unmount. */
export default function usePopupSession(enabled = true) {
  const store = useStore<RootState>();
  const auth = useAppSelector(state => state.auth);
  const key = enabled ? getSessionKey(auth) : undefined;
  const currentSessionRef = useRef({ key });
  const mountedRef = useRef(true);
  if (currentSessionRef.current.key !== key) currentSessionRef.current = { key };
  const session = currentSessionRef.current;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const isCurrent = useCallback(() => mountedRef.current && session.key !== undefined &&
    currentSessionRef.current === session && getSessionKey(store.getState().auth) === session.key, [session, store]);

  return { isActive: key !== undefined, isCurrent };
}
